import React from "react";
import { useAppDispatch, useAppSelector } from "../../../../redux";
import {
  Button,
  Dropdown,
  Iconography,
  IconographySizes,
  Loader,
} from "@hid-galaxy-ui/galaxy-react";
import StatusMessage from "./status-message";
import { useNavigate } from "react-router";
import {
  getFormFieldsEngagementSagaAction,
  submitFormEngagementSagaAction,
} from "../../../../redux/saga/engagement-form/saga-actions";
import { FormFieldType } from "../../../../redux/slices/engagement-form/Types";
import CustomTextField from "../../../../common/components/dynamic-form-fields/CustomTextField";
import CustomTextArea from "../../../../common/components/dynamic-form-fields/CustomTextArea";
import CustomRadio from "../../../../common/components/dynamic-form-fields/CustomRadio";
import CustomCheckBox from "../../../../common/components/dynamic-form-fields/CustomCheckBox";
import { LoaderSizes } from "@hid-galaxy-ui/galaxy-react/components/Loader/loaderEnums";
import { getAllPartnersSagaAction } from "../../../../redux/saga/partner-details/saga-actions";
import "../EngagementForm.scss";

const EngagementForm: React.FC<{}> = ({}) => {
  const [formData, setFormData] = React.useState<Record<string, any>>({});
  const [openAccordion, setOpenAccordion] = React.useState<number>(1);
  const [formSubmitted, setFormSubmitted] = React.useState<boolean>(false);
  const { formFieldsEngagement, status } = useAppSelector(
    (state) => state.engagement_form
  );
  const { loggedUserDetail } = useAppSelector((state) => state.auth);

  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  React.useEffect(() => {
    dispatch(getFormFieldsEngagementSagaAction());
    dispatch(getAllPartnersSagaAction());
  }, []);

  React.useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [openAccordion]);

  React.useEffect(() => {
    if (formFieldsEngagement?.length > 0) {
      setFormData(getInitialFormData(formFieldsEngagement));
    }
  }, [formFieldsEngagement]);

  const sortedFields = [...formFieldsEngagement].sort(
    (a, b) => a.sort_order - b.sort_order
  );

  const getInitialFormData = (fields: FormFieldType[]) => {
    const initialData: Record<string, any> = {};
    fields.forEach((field) => {
      switch (field.field_type) {
        case "text":
        case "textarea":
        case "radio":
          initialData[field.field_key] = "";
          break;
        case "checkbox":
          initialData[field.field_key] = [];
          break;
        default:
          initialData[field.field_key] = "";
      }
    });
    return initialData;
  };

  const handleChange = (key: string, value: string | string[]) => {
    setFormData((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const renderField = (field: FormFieldType) => {
    switch (field.field_type) {
      case "text":
        if (field.field_key === "partner_name")
          return (
            <PartnerNameDropDown
              key={field.field_key}
              field={field}
              formData={formData}
              setFormData={setFormData}
              handleChange={handleChange}
            />
          );
        else
          return (
            <CustomTextField
              key={field.field_key}
              fieldKey={field.field_key}
              fieldLabel={field.label}
              fieldValue={formData[field.field_key] || ""}
              isRequired={!!field.is_required}
              handleChange={handleChange}
            />
          );
      case "textarea":
        return (
          <CustomTextArea
            key={field.field_key}
            fieldKey={field.field_key}
            fieldLabel={field.label}
            fieldValue={formData[field.field_key] || ""}
            isRequired={!!field.is_required}
            handleChange={handleChange}
          />
        );
      case "radio":
        return (
          <CustomRadio
            key={field.field_key}
            fieldKey={field.field_key}
            fieldLabel={field.label}
            fieldValue={formData[field.field_key] || ""}
            options={field.metadata}
            isRequired={!!field.is_required}
            handleChange={handleChange}
          />
        );
      case "checkbox":
        return (
          <CustomCheckBox
            key={field.field_key}
            fieldKey={field.field_key}
            fieldLabel={field.label}
            fieldValue={formData[field.field_key] || ""}
            options={field.metadata}
            isRequired={!!field.is_required}
            handleChange={handleChange}
          />
        );
      default:
        return (
          <CustomTextField
            key={field.field_key}
            fieldKey={field.field_key}
            fieldLabel={field.label}
            fieldValue={formData[field.field_key] || ""}
            isRequired={!!field.is_required}
            handleChange={handleChange}
          />
        );
    }
  };

  const onSubmitClick = () => {
    const transformedData = sortedFields.map((field) => ({
      field_type: field.field_type,
      field_key: field.field_key,
      label: field.label,
      value: formData[field.field_key],
      sort_order: field.sort_order,
      is_required: field.is_required,
      metadata: field.metadata,
    }));

    dispatch(
      submitFormEngagementSagaAction({
        source: "Engagement",
        submitted_by: loggedUserDetail?.ID,
        data: transformedData,
      })
    );
    setFormSubmitted(true);
  };

  const isSubmitButtonEnabled = formFieldsEngagement.every((field) => {
    if (!field.is_required) return true;

    const value = formData[field.field_key];

    if (field.field_type === "checkbox") {
      return Array.isArray(value) && value.length > 0;
    }

    return value !== undefined && value !== null && value !== "";
  });

  return (
    <div className="engagement-form-container">
      {formSubmitted ? (
        <StatusMessage />
      ) : (
        <>
          <div className="form-wrapper">
            <h1 className="title">Submit New Engagement Form</h1>
            {status.formFieldsEngagementLoading ? (
              <div className="form-loading">
                <Loader variant={LoaderSizes.large} />
              </div>
            ) : (
              <>
                <form className="form-container">
                  {sortedFields.map((field) => {
                    return renderField(field);
                  })}
                </form>
                <div className="button-group">
                  <Button
                    variant="secondary"
                    label="Cancel"
                    onClick={() => {
                      navigate("/hid-admin/home");
                    }}
                  />
                  <Button
                    variant="primary"
                    label="Submit"
                    icon={<Iconography icon="arrowRight" />}
                    disabled={!isSubmitButtonEnabled}
                    onClick={() => {
                      onSubmitClick();
                    }}
                  />
                </div>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
};

const PartnerNameDropDown: React.FC<{
  field: FormFieldType;
  formData: Record<string, any>;
  setFormData: React.Dispatch<React.SetStateAction<Record<string, any>>>;
  handleChange: (key: string, value: string | string[]) => void;
}> = ({ field, formData, setFormData, handleChange }) => {
  const [selectedOption, setSelectedOption] = React.useState(null);
  const { allPartners } = useAppSelector((state) => state.partner_details);
  const existingPartnerOptions = React.useMemo(
    () =>
      allPartners.map((partner) => ({
        label: partner.partner_name,
        value: partner.partner_name,
      })),
    [allPartners]
  );
  const dropdownOptions = React.useMemo(
    () => [
      {
        label: "New Partner",
        value: "New Partner",
        options: [
          {
            label: "Enter New Partner Name",
            value: "Enter New Partner Name",
          },
        ],
      },
      {
        label: "Existing Partners",
        value: "Existing Partners",
        options: [...existingPartnerOptions],
      },
    ],
    [existingPartnerOptions]
  );

  const onDropdownSelect = (data: { label: string; value: string } | null) => {
    setSelectedOption(data);

    if (data?.value === "Enter New Partner Name") {
      handleChange(field.field_key, "");
    } else if (data?.value) {
      handleChange(field.field_key, data.value);

      const selectedPartner = allPartners.find(
        (partner) => partner.partner_name === data.value
      );

      if (selectedPartner) {
        setFormData((prevData) => {
          const updatedData = { ...prevData };

          Object.keys(selectedPartner).forEach((key) => {
            let value = selectedPartner[key as keyof typeof selectedPartner];

            if (key === "partner_type" && typeof value === "string") {
              value = value.split(",").map((s) => s.trim());
            }
            if (key === "partner_potential" && typeof value === "string") {
              value = value.toLowerCase() === "prime" ? "No" : "Yes";
            }

            if (key in updatedData) {
              updatedData[key] = value;
            }
          });

          return updatedData;
        });
      }
    } else {
      handleChange(field.field_key, "");
    }
  };
  return (
    <div className="partner-name-form-field">
      <Dropdown
        id="partnerName"
        label={field.label}
        placeholder="Partner"
        theme="dark"
        options={dropdownOptions}
        selected={selectedOption}
        isSearchable={true}
        isRequired={true}
        onSelect={(data) => {
          onDropdownSelect(data);
        }}
      />
      {selectedOption?.value === "Enter New Partner Name" && (
        <CustomTextField
          key={field.field_key}
          fieldKey={field.field_key}
          fieldLabel={"Enter New Partner Name"}
          fieldValue={formData[field.field_key] || ""}
          isRequired={!!field.is_required}
          handleChange={handleChange}
        />
      )}
    </div>
  );
};

export default EngagementForm;
